import { useState } from 'react';

export default function FinalCTA() {
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = formData.get('phone_alt');
    if (honeypot && String(honeypot).trim() !== '') {
      setStatus('success');
      form.reset();
      return;
    }
    try {
      const response = await fetch('https://readdy.ai/api/form/d9ammtijutvtlj0vtaj0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as any).toString(),
      });
      const responseText = await response.text();
      let parsed: any = null;
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }
      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam = serverMsg?.toLowerCase().includes('spam');
      if (response.ok && parsed?.code === 'OK' && !isSpam) {
        setStatus('success');
        setFormError('');
        form.reset();
      } else {
        setStatus('error');
        setFormError(serverMsg || 'Something went wrong. Please try again.');
      }
    } catch {
      setStatus('error');
      setFormError('Network error. Please try again.');
    }
  };

  return (
    <section id="contact" className="bg-primary-950 py-16 md:py-24">
      <div className="w-full px-4 md:px-8 lg:px-12">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-start">
            {/* Left: Copy */}
            <div>
              <p className="text-accent-400 text-sm font-semibold tracking-widest uppercase mb-3">
                Get Started
              </p>
              <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl text-background-50 leading-tight mb-6">
                Ready for Financial
                <span className="italic text-accent-400"> Clarity?</span>
              </h2>
              <p className="text-background-300 text-base md:text-lg leading-relaxed mb-8">
                Join independent restaurants, trades, small businesses, and nonprofits
                across Richmond, Henrico, and Chesterfield who trust Penland for
                proactive, industry-specific bookkeeping. Your free 30-minute discovery
                call includes a no-obligation assessment of your current books and a
                roadmap for improvement.
              </p>

              <div className="space-y-5 mb-10">
                {[
                  {
                    icon: 'ri-calendar-check-line',
                    title: 'Free 30-Minute Discovery Call',
                    desc: 'We review your current setup, identify quick wins, and outline a plan.',
                  },
                  {
                    icon: 'ri-shield-check-line',
                    title: 'No Pressure, No Obligation',
                    desc: 'You get honest feedback and a clear quote. Decide on your own timeline.',
                  },
                  {
                    icon: 'ri-user-smile-line',
                    title: 'Speak With Your Future Bookkeeper',
                    desc: 'Meet the person who will actually handle your books — not a salesperson.',
                  },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-accent-500/20 flex items-center justify-center shrink-0">
                      <i className={`${item.icon} text-accent-300 text-lg`} />
                    </div>
                    <div>
                      <h4 className="text-background-50 font-semibold text-sm md:text-base mb-1">
                        {item.title}
                      </h4>
                      <p className="text-background-400 text-sm">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right: Form */}
            <div className="bg-background-50 rounded-2xl p-6 md:p-8">
              <h3 className="font-heading text-xl md:text-2xl text-primary-950 font-semibold mb-2">
                Request Free Consultation
              </h3>
              <p className="text-foreground-500 text-sm mb-6">
                Fill out the form below and we will respond within one business day.
              </p>

              <form data-readdy-form onSubmit={handleSubmit} className="space-y-4">
                <input type="text" name="phone_alt" className="hp-field" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-foreground-700 text-sm font-medium mb-1.5">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      required
                      className="w-full bg-background-100 border border-background-200 rounded-lg px-4 py-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-accent-500/40"
                      placeholder="John Smith"
                    />
                  </div>
                  <div>
                    <label className="block text-foreground-700 text-sm font-medium mb-1.5">
                      Business Name *
                    </label>
                    <input
                      type="text"
                      name="business_name"
                      required
                      className="w-full bg-background-100 border border-background-200 rounded-lg px-4 py-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-accent-500/40"
                      placeholder="Your Business LLC"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-foreground-700 text-sm font-medium mb-1.5">
                    Business Type *
                  </label>
                  <select
                    name="business_type"
                    required
                    defaultValue=""
                    className="w-full bg-background-100 border border-background-200 rounded-lg px-4 py-3 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-accent-500/40"
                  >
                    <option value="" disabled>Select your industry</option>
                    <option value="restaurant">Restaurant / Cafe / Bar / Catering</option>
                    <option value="trades">Skilled Trades / Contractor</option>
                    <option value="small_business">Small Business</option>
                    <option value="nonprofit">Nonprofit</option>
                    <option value="both">Other</option>
                  </select>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-foreground-700 text-sm font-medium mb-1.5">
                      Email *
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      className="w-full bg-background-100 border border-background-200 rounded-lg px-4 py-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-accent-500/40"
                      placeholder="john@yourbusiness.com"
                    />
                  </div>
                  <div>
                    <label className="block text-foreground-700 text-sm font-medium mb-1.5">
                      Phone
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      className="w-full bg-background-100 border border-background-200 rounded-lg px-4 py-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-accent-500/40"
                      placeholder="(540) 123-4567"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-foreground-700 text-sm font-medium mb-1.5">
                    What challenges or goals can we help with? (optional)
                  </label>
                  <textarea
                    name="message"
                    rows={3}
                    maxLength={500}
                    className="w-full bg-background-100 border border-background-200 rounded-lg px-4 py-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-accent-500/40 resize-none"
                    placeholder="E.g., struggling with tipped payroll compliance, need job costing setup, behind on books..."
                  />
                  <p className="text-foreground-400 text-xs mt-1">Max 500 characters</p>
                </div>

                <button
                  type="submit"
                  className="w-full bg-accent-500 hover:bg-accent-600 text-primary-950 text-sm md:text-base font-bold px-6 py-4 rounded-lg transition-all whitespace-nowrap"
                >
                  Request Free Consultation
                  <i className="ri-arrow-right-line ml-2" />
                </button>
              </form>

              {status === 'success' && (
                <div className="mt-4 p-4 bg-accent-100 rounded-lg">
                  <p className="text-accent-800 text-sm font-medium">
                    <i className="ri-check-line mr-2" />
                    Thank you! We will reach out within one business day to schedule your free consultation.
                  </p>
                </div>
              )}
              {status === 'error' && formError && (
                <div className="mt-4 p-4 bg-red-50 rounded-lg">
                  <p className="text-red-700 text-sm">{formError}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}